import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Announcement } from 'types/announcements';
import { AppDispatch, RootState } from 'redux/store';
import DeleteModal from 'components/Common/DeleteModal';
import Table from 'components/Common/StyledTable';
import routes from 'constants/routes';
import { dateFormat, timeFormat } from 'utils/helpers';
import { deleteAnnouncementById } from 'redux/slices/announcements/announcementsSlice';
import { selectSelectedParishId, selectEffectiveStaffRole } from 'redux/selectors/parishContext';
import { hasAtLeastRole } from 'api/v1Helpers';

const AnnouncementColumns = [
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing StyledTable column render signature
    { id: 'createDate', label: 'Date', render: ({ value }: any) => dateFormat(value) },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing StyledTable column render signature
    { id: 'createTime', label: 'Time', render: ({ data }: any) => timeFormat(data?.['createDate']) },
    { id: 'title', label: 'Title' },
];

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing loosely-typed data/loading props
function AnnouncementContainer({ data, loading }: any) {
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const parishId = useSelector(selectSelectedParishId);
    const effectiveRole = useSelector(selectEffectiveStaffRole);
    const canManageAnnouncements = hasAtLeastRole(effectiveRole, 'MERCHANT_ADMIN');
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [announcement, setAnnouncement] = useState<Announcement | null>(null);
    const { totalCount } = useSelector((state: RootState) => ({
        totalCount: state.announcement.announcementsList?.totalCount,
    }));

    const navigationHandler = useCallback(
        (row: Announcement) => {
            navigate(routes.announcements.details.replace(':id', row?.id));
        },
        [navigate]
    );

    const handleDeleteClick = useCallback((row: Announcement) => {
        setOpenDeleteModal(true);
        setAnnouncement(row);
    }, []);

    const deleteAnnouncement = useCallback(async () => {
        if (!parishId || !announcement?.id || !canManageAnnouncements) return;
        setDeleteLoading(true);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing dispatched thunk payload union
        const { payload }: any = await dispatch(deleteAnnouncementById({ parishId, id: announcement.id }));
        setDeleteLoading(false);
        if (payload?.ok) {
            setOpenDeleteModal(false);
        }
    }, [dispatch, announcement?.id, parishId, canManageAnnouncements]);

    return (
        <div>
            <Table
                unStyled
                showDeleteCell={canManageAnnouncements}
                serverSidePagination
                totalCount={totalCount}
                columns={AnnouncementColumns}
                data={data}
                navigationHandler={navigationHandler}
                onDeleteClick={handleDeleteClick}
                loading={loading}
            />
            <DeleteModal
                isOpen={openDeleteModal}
                title="Delete Announcement"
                description="Are you sure you want to delete the selected announcement!"
                onClose={() => setOpenDeleteModal(false)}
                onDelete={deleteAnnouncement}
                loading={deleteLoading}
            />
        </div>
    );
}

export default AnnouncementContainer;
